/**
 * Per-request proof of possession (ADR 0040, docs/plan/14 §1).
 *
 * The session cookies prove nothing on their own any more: every request
 * inside `RequireSession` carries `X-Session-Proof`, an ECDSA P-256 signature
 * made by a key that lives in this browser profile's IndexedDB and **cannot be
 * read by script** (`extractable: false`). A `Cookie:` header captured in the
 * shared edge's audit log is therefore inert — the attacker has the cookies and
 * no way to produce a signature.
 *
 * ## The exact canonical string
 *
 * ```
 * X-Session-Proof: v1.<b64url(payload)>.<b64url(sig)>
 *
 * payload = JSON, no whitespace, keys in EXACTLY this order:
 *   {"kid":"<43 chars>","m":"POST","p":"/api/v1/…","iat":1789034521,"jti":"<b64url of 16 bytes>"}
 *
 * sig = ECDSA-P256-SHA256 over ASCII( "ols/proof/v1." ‖ b64url(payload) )
 *       WebCrypto's raw r‖s, 64 bytes
 * ```
 *
 * Key order is not cosmetic: the server re-reads the payload it was given, so
 * a mismatch would only surface as a signature failure on a server that
 * re-serialised. The payload is therefore built by string concatenation rather
 * than by `JSON.stringify` on an object literal, because object key order is a
 * property of the engine, not of the source.
 *
 * ## What is and is not in it
 *
 * - `m` and `p` bind the proof to *this* request: one captured on
 *   `GET …/messages` cannot authorise `POST …/send`, even inside the replay
 *   window. `p` is the path only — never the query string, which the edge is
 *   free to normalise and which carries nothing security-relevant of ours
 *   except the single-use stream ticket (ADR 0041), itself bound server-side.
 * - `jti` is 16 fresh random bytes per request. The server writes it with
 *   `SET … NX EX 300`; a second presentation is `401 proof_replayed`. Every
 *   request gets its own, safe methods included.
 * - `iat` comes from the **server's** clock, corrected by an offset this module
 *   keeps (see {@link noteServerTime}). A device whose clock is hours wrong
 *   still works; one that jumps mid-session gets one automatic correction from
 *   `401 proof_stale`'s `details.server_time`.
 * - The **body is not signed** and neither is the session id. TLS covers
 *   in-flight integrity, `Idempotency-Key` covers duplicate execution, and the
 *   session id is something a cookie thief already has.
 *
 * ## Realm-agnostic on purpose
 *
 * Nothing here touches `document`, `window` or React: the service worker
 * imports this module to sign a queued send at replay time (doc 06 §10), and
 * the popup message window (ADR 0020) shares the same profile key.
 */

/** Request header the backend reads the proof from (docs/plan/14 §5.1). */
export const PROOF_HEADER = 'X-Session-Proof';

/** Version prefix of the header value, and of the signing context. */
export const PROOF_VERSION = 'v1';

/**
 * Domain-separation prefix for the signed bytes.
 *
 * The signature is over `"ols/proof/v1." ‖ b64url(payload)`, never over the
 * payload alone: a bare-payload signature could in principle be reused as a
 * signature over some other structure this key is later asked to produce.
 */
export const PROOF_CONTEXT = 'ols/proof/v1.';

/** WebCrypto parameters — fixed by ADR 0040, not configurable. */
export const PROOF_KEY_ALGORITHM = { name: 'ECDSA', namedCurve: 'P-256' } as const;
export const PROOF_SIGN_ALGORITHM = { name: 'ECDSA', hash: 'SHA-256' } as const;

/** Public half of the device key, as enrolled in the login/rebind body. */
export interface DeviceKeyJwk {
  readonly kty: 'EC';
  readonly crv: 'P-256';
  readonly x: string;
  readonly y: string;
}

/** What {@link createProof} needs: a signing handle and its thumbprint. */
export interface ProofSigner {
  /** RFC 7638 thumbprint of the public JWK, 43 base64url characters. */
  readonly kid: string;
  /** Non-extractable private key, usages `["sign"]`. */
  readonly privateKey: CryptoKey;
}

/* ------------------------------------------------------------ base64url */

/** Unpadded base64url of raw bytes. */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * UTF-8 bytes of a string, in a buffer WebCrypto will accept.
 *
 * The copy is not ceremony: `TextEncoder.encode` is typed over
 * `ArrayBufferLike`, which includes `SharedArrayBuffer`, and `subtle.sign`
 * refuses shared memory. Re-wrapping pins it to a plain `ArrayBuffer`.
 */
function utf8(value: string): Uint8Array<ArrayBuffer> {
  return new Uint8Array(new TextEncoder().encode(value));
}

/* --------------------------------------------------------- clock offset */

/**
 * Milliseconds to add to `Date.now()` to get the server's clock.
 *
 * Module-level rather than per-transport: a page, its popup and the service
 * worker each keep their own, and within one realm every signer wants the same
 * correction. It is never persisted — a stale offset from a previous session
 * is worse than no offset, and re-syncing costs one whoami.
 */
let clockOffsetMs = 0;

/**
 * Records the server's clock from a `server_time` value.
 *
 * Sources: the `Session` body of whoami/login/refresh, and
 * `details.server_time` on a `401 proof_stale`. Anything unparseable is
 * ignored — a corrupt value must not push every subsequent proof out of the
 * window.
 */
export function noteServerTime(serverTime: string | number | undefined | null): boolean {
  if (serverTime === undefined || serverTime === null) return false;
  const parsed = typeof serverTime === 'number' ? serverTime : Date.parse(serverTime);
  if (!Number.isFinite(parsed)) return false;
  clockOffsetMs = parsed - Date.now();
  return true;
}

/** The current correction, in milliseconds. Exposed for tests and diagnostics. */
export function clockOffset(): number {
  return clockOffsetMs;
}

/** Forgets the correction. Called on logout, and between tests. */
export function resetClockOffset(): void {
  clockOffsetMs = 0;
}

/** Corrected unix seconds — what `iat` is stamped from. */
export function proofNow(): number {
  return Math.floor((Date.now() + clockOffsetMs) / 1000);
}

/* ------------------------------------------------------------- payload */

export interface ProofPayload {
  readonly kid: string;
  readonly m: string;
  readonly p: string;
  readonly iat: number;
  readonly jti: string;
}

/**
 * The payload, serialised in the one order the contract specifies.
 *
 * `JSON.stringify` on each *value* (so quoting and escaping are the standard
 * ones) but the object is assembled by hand, so the key order is a property of
 * this function rather than of the engine.
 */
export function canonicalProofPayload(payload: ProofPayload): string {
  return (
    '{"kid":' +
    JSON.stringify(payload.kid) +
    ',"m":' +
    JSON.stringify(payload.m) +
    ',"p":' +
    JSON.stringify(payload.p) +
    ',"iat":' +
    String(payload.iat) +
    ',"jti":' +
    JSON.stringify(payload.jti) +
    '}'
  );
}

/** 16 random bytes, base64url. Fresh for every single request. */
export function newJti(): string {
  const bytes = new Uint8Array(16);
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (cryptoObj?.getRandomValues === undefined) {
    // No CSPRNG means no usable WebCrypto, so no key and no proof either; this
    // branch exists so the module never throws while degrading (ADR 0042).
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  } else {
    cryptoObj.getRandomValues(bytes);
  }
  return base64UrlEncode(bytes);
}

/**
 * The method as the server will see it. Upper-case, always.
 */
export function canonicalMethod(method: string): string {
  return method.toUpperCase();
}

/**
 * The path as the server will compare it (`r.URL.EscapedPath()`).
 *
 * Path only — the query is dropped here so no caller can accidentally sign
 * one. The client already sends canonical paths (no dot segments, no doubled
 * slashes, ids URL-safe per ADR 0012), so edge normalisation is a no-op; this
 * function does not re-normalise, because a client that "fixed" a path the
 * server would not fix would sign something the server never sees.
 */
export function canonicalPath(url: string): string {
  const separator = url.indexOf('?');
  const withoutQuery = separator < 0 ? url : url.slice(0, separator);
  const hash = withoutQuery.indexOf('#');
  const target = hash < 0 ? withoutQuery : withoutQuery.slice(0, hash);
  // Absolute URL: take its pathname. Relative: it already is the path.
  const schemeEnd = target.indexOf('://');
  if (schemeEnd < 0) return target;
  const pathStart = target.indexOf('/', schemeEnd + 3);
  return pathStart < 0 ? '/' : target.slice(pathStart);
}

/* ------------------------------------------------------------- signing */

/**
 * Signs one request and returns the `X-Session-Proof` header value.
 *
 * Throws only if WebCrypto itself fails; callers treat a throw the way they
 * treat a missing key — the request goes out unbound and the server decides
 * (ADR 0042), because swallowing it into a silent retry loop is the failure
 * mode that ADR forbids.
 */
export async function createProof(
  signer: ProofSigner,
  method: string,
  url: string,
  options: { readonly iat?: number; readonly jti?: string } = {},
): Promise<string> {
  const payload: ProofPayload = {
    kid: signer.kid,
    m: canonicalMethod(method),
    p: canonicalPath(url),
    iat: options.iat ?? proofNow(),
    jti: options.jti ?? newJti(),
  };
  const encodedPayload = base64UrlEncode(utf8(canonicalProofPayload(payload)));
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) throw new Error('WebCrypto is unavailable in this context.');
  const signature = await subtle.sign(
    PROOF_SIGN_ALGORITHM,
    signer.privateKey,
    utf8(PROOF_CONTEXT + encodedPayload),
  );
  return `${PROOF_VERSION}.${encodedPayload}.${base64UrlEncode(new Uint8Array(signature))}`;
}

/**
 * Parses a header value back into its payload. Verification is the server's
 * job; this exists so tests and diagnostics can read what was sent without
 * re-implementing the encoding.
 */
export function decodeProof(header: string): ProofPayload | undefined {
  const parts = header.split('.');
  if (parts.length !== 3 || parts[0] !== PROOF_VERSION) return undefined;
  const encoded = parts[1];
  if (encoded === undefined) return undefined;
  try {
    const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    const record = parsed as Record<string, unknown>;
    if (
      typeof record.kid !== 'string' ||
      typeof record.m !== 'string' ||
      typeof record.p !== 'string' ||
      typeof record.iat !== 'number' ||
      typeof record.jti !== 'string'
    ) {
      return undefined;
    }
    return { kid: record.kid, m: record.m, p: record.p, iat: record.iat, jti: record.jti };
  } catch {
    return undefined;
  }
}

/* ---------------------------------------------------------- thumbprint */

/**
 * RFC 7638 JWK thumbprint of a P-256 public key.
 *
 * The canonical member set for `EC` is `{crv, kty, x, y}`, in lexicographic
 * order, no whitespace — this is the one JSON in the system whose exact bytes
 * are defined by a standard rather than by us.
 */
export async function jwkThumbprint(jwk: DeviceKeyJwk): Promise<string> {
  const canonical = `{"crv":${JSON.stringify(jwk.crv)},"kty":${JSON.stringify(
    jwk.kty,
  )},"x":${JSON.stringify(jwk.x)},"y":${JSON.stringify(jwk.y)}}`;
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) throw new Error('WebCrypto is unavailable in this context.');
  const digest = await subtle.digest('SHA-256', utf8(canonical));
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Narrows an exported JWK to the four members that are enrolled.
 *
 * WebCrypto's export carries `ext`, `key_ops` and friends; none of them belong
 * on the wire, and the thumbprint is defined over exactly these four.
 */
export function toDeviceKeyJwk(exported: JsonWebKey): DeviceKeyJwk | undefined {
  if (exported.kty !== 'EC' || exported.crv !== 'P-256') return undefined;
  if (typeof exported.x !== 'string' || typeof exported.y !== 'string') return undefined;
  return { kty: 'EC', crv: 'P-256', x: exported.x, y: exported.y };
}
